<template>
  <div>
    <v-btn
      v-auth-click="{
        roles: ['simple-user', 'licence-manager'],
        message:
          'Nur Nutzer mit der Rolle license-manager können GeoPackage-Dateien hinzufügen!',
      }"
      color="white"
      class="geo-upload-btn w-100"
      :prepend-icon="buttonIcon"
      @click="handleUploadClick"
    >
      Layer hinzufügen
    </v-btn>

    <v-dialog
      v-model="showUploadDialog"
      max-width="800px"
      v-if="
        authStore.hasRole('simple-user') || authStore.hasRole('licence-manager')
      "
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">GeoPackage Layer importieren</span>
        </v-card-title>

        <v-card-text>
          <v-stepper v-model="step" editable>
            <v-stepper-header>
              <v-stepper-item :value="1" :complete="step > 1"
                >Datei hochladen (Schritt 1)</v-stepper-item
              >
              <v-divider></v-divider>
              <v-stepper-item :value="2" :complete="step > 2"
                >Layer auswählen (Schritt 2)</v-stepper-item
              >
              <v-divider></v-divider>
              <v-stepper-item :value="3"
                >Layer konfigurieren (Schritt 3)</v-stepper-item
              >
            </v-stepper-header>

            <v-stepper-window>
              <!-- Step 1: Upload File -->
              <v-stepper-window-item :value="1">
                <v-alert
                  v-if="uploadSuccess"
                  type="success"
                  dense
                  dismissible
                  @click:close="uploadSuccess = ''"
                >
                  {{ uploadSuccess }}
                </v-alert>

                <v-alert type="info" dense class="mb-4">
                  <strong>Hinweis zu benutzerdefinierten Symbolen:</strong
                  ><br />
                  Wenn Ihre GeoPackage-Datei benutzerdefinierte Bilddateien für
                  Markierungen (z.B. PNG-Icons) enthält, stellen Sie bitte
                  sicher, dass sich die Bilddateien im selben Verzeichnis wie
                  die GeoPackage-Datei befinden. Nur so kann die Anwendung die
                  benutzerdefinierten Symbole korrekt anzeigen.
                </v-alert>

                <v-file-input
                  v-model="fileInput"
                  accept=".gpkg,.geopackage"
                  label="GeoPackage Datei auswählen"
                  :loading="isUploading"
                  @change="handleFileSelect"
                  show-size
                  chips
                  prepend-icon="mdi-map"
                  :error-messages="uploadError"
                ></v-file-input>

                <v-checkbox
                  v-model="overwriteExisting"
                  label="Bestehende Datei mit demselben Namen überschreiben"
                ></v-checkbox>

                <v-row class="mt-3">
                  <v-spacer></v-spacer>
                  <v-btn
                    :disabled="!fileInput || isUploading"
                    class="mr-5 mb-5"
                    color="primary"
                    @click="uploadGeoPackage"
                    :loading="isUploading"
                  >
                    Hochladen
                  </v-btn>
                </v-row>
              </v-stepper-window-item>

              <!-- Step 2: Select Layers -->
              <v-stepper-window-item :value="2">
                <v-alert v-if="geoPackageStore.error" type="error" dense>
                  {{ geoPackageStore.error }}
                </v-alert>

                <div v-else-if="geoPackageStore.files.length > 0">
                  <div
                    v-for="geoPackage in geoPackageStore.files"
                    :key="geoPackage.id"
                    class="mb-4 pa-3 rounded border"
                  >
                    <div class="d-flex align-center justify-space-between mb-3">
                      <h3>GeoPackage: {{ geoPackage.file_name }}</h3>
                      <v-btn
                        icon="mdi-delete"
                        size="small"
                        color="error"
                        variant="text"
                        @click="confirmDelete(geoPackage)"
                      >
                      </v-btn>
                    </div>

                    <v-list
                      select-strategy="independent"
                      v-model:selected="selectedLayerIds"
                      multiple
                      class="bg-transparent"
                    >
                      <v-list-item
                        v-for="layer in filteredLayers(geoPackage.id)"
                        :key="layer.id"
                        :value="layer.id"
                        class="border-b"
                      >
                        <template v-slot:prepend>
                          <v-list-item-action start>
                            <v-checkbox-btn
                              :model-value="selectedLayerIds.includes(layer.id)"
                            ></v-checkbox-btn>
                          </v-list-item-action>
                          <v-icon
                            :color="getGeometryTypeColor(layer.geometry_type)"
                            class="ml-2"
                          >
                            {{ getGeometryTypeIcon(layer.geometry_type) }}
                          </v-icon>
                        </template>

                        <v-list-item-title>{{
                          layer.layer_name
                        }}</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ layer.geometry_type }} -
                          {{ layer.feature_count }} features
                        </v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item
                        v-if="
                          geoPackageStore.getLayersForFile(geoPackage.id)
                            .length === 0
                        "
                      >
                        <v-list-item-title class="text-grey"
                          >Keine Layer verfügbar für diese GeoPackage
                          Datei</v-list-item-title
                        >
                      </v-list-item>
                    </v-list>
                  </div>
                </div>
                <div v-else>
                  <v-alert type="info" dense>
                    Keine GeoPackage Dateien hochgeladen. Bitte laden Sie eine
                    Datei in Schritt 1 hoch.
                  </v-alert>
                </div>

                <v-row class="mt-3">
                  <v-btn variant="text" @click="step = 1"> Zurück </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    class="mr-5 mb-5"
                    color="primary"
                    :disabled="selectedLayerIds.length === 0"
                    @click="goToStep3()"
                  >
                    Weiter
                  </v-btn>
                </v-row>
              </v-stepper-window-item>

              <!-- Step 3: Configure Layers -->
              <v-stepper-window-item :value="3">
                <v-card-text>
                  <h3 class="mb-3">Layer Settings</h3>

                  <div v-if="geoPackageStore.getSelectedLayers.length === 0">
                    <v-alert type="warning" dense>
                      Keine Layer ausgewählt. Bitte gehen Sie zu Schritt 2 und
                      wählen Sie mindestens einen Layer aus.
                    </v-alert>
                  </div>

                  <div v-else>
                    <div
                      v-for="layer in geoPackageStore.getSelectedLayers"
                      :key="layer.id"
                    >
                      <v-card v-if="layerStyles[layer.id]" class="mb-3 pa-3">
                        <div class="d-flex justify-space-between align-center">
                          <v-card-title class="text-subtitle-1 px-0">
                            {{ layer.layer_name }}
                          </v-card-title>
                          <v-btn
                            icon
                            size="small"
                            color="error"
                            variant="text"
                            @click="removeLayerFromSelection(layer.id)"
                          >
                            <v-icon>mdi-close</v-icon>
                          </v-btn>
                        </div>

                        <v-text-field
                          v-model="layerDisplayNames[layer.id]"
                          label="Display Name"
                          dense
                        ></v-text-field>

                        <!-- Show info alert for layers with custom icons -->
                        <v-alert
                          v-if="layerStyles[layer.id].icon"
                          type="info"
                          dense
                          class="mb-3"
                        >
                          Dieser Layer verwendet ein benutzerdefiniertes Icon.
                          Farbe und Deckkraft können nicht angepasst werden.
                        </v-alert>

                        <!-- Only show color and opacity controls for layers without custom icons -->
                        <template v-if="!layerStyles[layer.id].icon">
                          <v-select
                            v-model="layerStyles[layer.id].color"
                            label="Farbe"
                            :items="colorOptions"
                            item-title="text"
                            item-value="value"
                            dense
                          ></v-select>

                          <v-slider
                            v-model="layerStyles[layer.id].opacity"
                            label="Deckkraft"
                            min="0"
                            max="1"
                            step="0.1"
                            thumb-label
                          ></v-slider>
                        </template>
                      </v-card>
                    </div>
                  </div>
                  <div v-if="isImporting" class="mt-4">
                    <v-progress-linear
                      :value="importProgress"
                      color="primary"
                      height="10"
                      striped
                    ></v-progress-linear>

                    <div class="text-center mt-2">
                      <v-chip color="primary" class="px-2 py-1" outlined>
                        <v-icon left small>mdi-map-marker</v-icon>
                        Lädt {{ currentLayerName }}
                        <span class="ml-2 font-weight-bold"
                          >{{ currentLayerIndex }}/{{
                            selectedLayerIds.length
                          }}</span
                        >
                      </v-chip>
                    </div>
                  </div>
                </v-card-text>

                <v-row>
                  <v-btn variant="text" @click="step = 2"> Zurück </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn
                    class="mr-5 mb-5"
                    color="primary"
                    @click="importLayers"
                    :loading="isImporting"
                  >
                    Layer importieren
                  </v-btn>
                </v-row>
              </v-stepper-window-item>
            </v-stepper-window>
          </v-stepper>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" color="grey darken-1" @click="closeDialog">
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5"> GeoPackage löschen? </v-card-title>
        <v-card-text>
          <v-alert type="warning" dense class="mb-3">
            <strong>Achtung:</strong> Diese Aktion kann nicht rückgängig gemacht
            werden.
          </v-alert>
          <p>
            Möchten Sie die GeoPackage-Datei
            <strong>{{ geoPackageToDelete?.file_name }}</strong>
            wirklich löschen?
          </p>
          <p class="mt-2">
            Alle Layer dieser Datei werden ebenfalls aus der Datenbank entfernt.
            Wenn Sie diese Layer erneut verwenden möchten, müssen Sie die Datei
            erneut hochladen.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false"> Abbrechen </v-btn>
          <v-btn
            color="error"
            variant="flat"
            @click="deleteGeoPackage"
            :loading="isDeleting"
          >
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import * as L from "leaflet";
import { mapStores } from "pinia";
import { useMapStore } from "../store/mapStore";
import { useGeoPackageStore } from "../store/geoPackageStore";
import { useOfficeStore } from "../store/officeStore";
import { useAuthStore } from "../store/authStore";
import { GeoPackageService } from "../services/geopackage-service";
import { simplifyGeoJson } from "../utils/geo-utils";
import {
  getDefaultStyle,
  convertQmlStyleToGeoPackageStyle,
} from "../utils/geopackage-style-utils";
import { eventBus } from "../utils/eventBus";

export default defineComponent({
  name: "GeoPackageUploader",

  props: {
    mapUtilsObj: { type: Object, default: null },
  },
  setup() {
    const authStore = useAuthStore();
    const mapStore = useMapStore();
    const geoPackageStore = useGeoPackageStore();
    const officeStore = useOfficeStore();

    return {
      authStore,
      mapStore,
      geoPackageStore,
      officeStore,
    };
  },

  data() {
    return {
      showUploadDialog: false,
      fileInput: null as File | null,
      step: 1,
      uploadError: "",
      uploadSuccess: "",
      overwriteExisting: false,
      isUploading: false,
      isImporting: false,
      isDeleting: false,
      showDeleteDialog: false,
      geoPackageToDelete: null as any,
      selectedLayerIds: [] as number[],
      layerDisplayNames: {} as Record<number, string>,
      layerStyles: {} as Record<
        number,
        { color: string; opacity: number; icon?: string }
      >,
      colorOptions: [
        { text: "Blue", value: "#3388ff" },
        { text: "Red", value: "#ff3333" },
        { text: "Green", value: "#33ff33" },
        { text: "Yellow", value: "#ffff33" },
        { text: "Purple", value: "#8833ff" },
        { text: "Orange", value: "#ff8833" },
      ],
      buttonIcon: "mdi-map-plus",
      importProgress: 0,
      currentLayerName: "",
      currentLayerIndex: 0,
      apiBaseUrl: "http://localhost:5005/jmapi/v1", // Direct to backend for image requests
    };
  },

  computed: {
    selectedFile() {
      return this.geoPackageStore.getSelectedFile;
    },

    filteredLayers() {
      // Filter out non-visual layers (like layer_styles metadata tables)
      return (fileId: number) => {
        const layers = this.geoPackageStore.getLayersForFile(fileId);
        return layers.filter(
          (layer) =>
            layer.layer_name !== "layer_styles" &&
            layer.geometry_type !== "None",
        );
      };
    },
  },

  watch: {
    selectedLayerIds(newIds) {
      // Sync selectedLayerIds with the store whenever it changes
      this.geoPackageStore.selectLayers(newIds);
    },
  },

  methods: {
    async handleUploadClick() {
      this.showUploadDialog = true;

      try {
        // check if geopackagefiles are already uploaded
        await this.geoPackageStore.fetchGeoPackages(
          this.officeStore.selectedProject.id,
        );
        const files = this.geoPackageStore.files;

        if (files && files.length > 0) {
          // Create a promise array for fetching layers for each file
          const fetchPromises = files.map((file) =>
            this.geoPackageStore.fetchGeoPackageLayers(file.id),
          );

          // Wait for all layer fetches to complete
          await Promise.all(fetchPromises);

          // Start at step 2 (layer selection) when files already exist
          this.step = 2;
        }
      } catch (error) {
        console.error("Error fetching layers:", error);
      } finally {
        this.isLoading = false;
      }
    },

    handleFileSelect(event: Event) {
      this.uploadError = "";
      if (event && event.target) {
        // Cast the event.target to HTMLInputElement
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
          this.fileInput = input.files[0];
        } else {
          this.fileInput = null;
        }
      } else {
        this.fileInput = null;
      }
    },

    async uploadGeoPackage() {
      if (!this.fileInput) return;

      this.isUploading = true;
      this.uploadError = "";
      this.uploadSuccess = "";

      try {
        const projectId = this.officeStore.selectedProject.id;
        const fileName = this.fileInput.name;
        const result = await this.geoPackageStore.uploadGeoPackage(
          this.fileInput,
          projectId,
          this.overwriteExisting,
        );

        // Fetch all GeoPackage files for the project
        await this.geoPackageStore.fetchGeoPackages(projectId);

        // Initialize display names and styles for each layer
        if (this.selectedFile) {
          this.selectedFile.layers.forEach((layer) => {
            this.layerDisplayNames[layer.id] = layer.layerName;
            this.layerStyles[layer.id] = {
              color: this.getDefaultColorForGeometryType(layer.geometry_type),
              opacity: 0.8,
            };
          });
        }
        const files = this.geoPackageStore.files;
        if (files && files.length > 0) {
          // Create a promise array for fetching layers for each file
          const fetchPromises = files.map((file) =>
            this.geoPackageStore.fetchGeoPackageLayers(file.id),
          );

          // Wait for all layer fetches to complete
          await Promise.all(fetchPromises);

          // Show success message
          this.uploadSuccess = `GeoPackage "${fileName}" wurde erfolgreich hochgeladen. Bitte wählen Sie die Layer aus, die Sie importieren möchten.`;

          // Clear the file input for next upload
          this.fileInput = null;

          // Wait 3 seconds before advancing to step 2 so user can read the message
          setTimeout(() => {
            this.step = 2;
          }, 3000);
        }
      } catch (error) {
        this.uploadError = error.message || "Upload failed";
      } finally {
        this.isUploading = false;
      }
    },

    goToStep3() {
      try {
        // Make sure we have selected layers
        if (this.selectedLayerIds.length === 0) {
          return;
        }

        // Update the selected layers in the store
        this.geoPackageStore.selectLayers(this.selectedLayerIds);

        // Initialize display names and styles for selected layers
        this.initializeLayerSettings();

        // Move to step 3
        this.step = 3;
      } catch (error) {
        console.error("Error in goToStep3:", error);
      }
    },

    removeLayerFromSelection(layerId: number) {
      // Remove from selectedLayerIds
      const index = this.selectedLayerIds.indexOf(layerId);
      if (index > -1) {
        this.selectedLayerIds.splice(index, 1);
      }

      // Remove from layerDisplayNames
      if (this.layerDisplayNames[layerId]) {
        delete this.layerDisplayNames[layerId];
      }

      // Remove from layerStyles
      if (this.layerStyles[layerId]) {
        delete this.layerStyles[layerId];
      }

      // The watcher will automatically sync with the store
    },

    initializeLayerSettings() {
      try {
        const selectedLayers = this.geoPackageStore.getSelectedLayers;

        if (!selectedLayers || selectedLayers.length === 0) {
          return;
        }

        selectedLayers.forEach((layer) => {
          if (!layer) {
            return;
          }

          const layerId = layer.id;

          if (!this.layerDisplayNames[layerId]) {
            this.layerDisplayNames[layerId] = layer.layer_name;
          }

          if (!this.layerStyles[layerId]) {
            // Try to convert backend QML style format
            let convertedStyle = null;
            if (layer.style && Object.keys(layer.style).length > 0) {
              convertedStyle = convertQmlStyleToGeoPackageStyle(layer.style);
            }

            // Merge converted style with defaults
            const defaultStyle = getDefaultStyle(layer.geometry_type);

            if (convertedStyle && Object.keys(convertedStyle).length > 0) {
              // Merge: defaults first, then converted style overrides
              this.layerStyles[layerId] = {
                ...defaultStyle,
                ...convertedStyle,
              };
            } else {
              // Use style utility for proper defaults
              this.layerStyles[layerId] = defaultStyle;
            }
          }
        });
      } catch (error) {
        console.error("Error in initializeLayerSettings:", error);
      }
    },

    detectLayerStyleFromGeometry(geometryType: string) {
      // Get a suitable default style based on geometry type
      const lowerGeom = geometryType.toLowerCase();

      if (lowerGeom.includes("point")) {
        return {
          color: this.getDefaultColorForGeometryType(geometryType),
          radius: 8,
          weight: 1,
          opacity: 0.8,
          fillOpacity: 0.8,
          outlineColor: "#ffffff",
        };
      } else if (lowerGeom.includes("line")) {
        return {
          color: this.getDefaultColorForGeometryType(geometryType),
          weight: 2,
          opacity: 0.8,
          dashArray: "",
        };
      } else if (lowerGeom.includes("polygon")) {
        return {
          color: "#333333", // Outline color
          fillColor: this.getDefaultColorForGeometryType(geometryType),
          weight: 2,
          opacity: 0.8,
          fillOpacity: 0.6,
        };
      }

      // Generic default
      return {
        color: this.getDefaultColorForGeometryType(geometryType),
        weight: 2,
        opacity: 0.8,
      };
    },

    getGeometryTypeIcon(geometryType: string): string {
      switch (geometryType.toLowerCase()) {
        case "point":
        case "multipoint":
          return "mdi-map-marker";
        case "linestring":
        case "multilinestring":
          return "mdi-vector-line";
        case "polygon":
        case "multipolygon":
          return "mdi-vector-polygon";
        case "None":
          return "mdi-vector-square";
        default:
          return "mdi-vector-square";
      }
    },

    getGeometryTypeColor(geometryType: string): string {
      switch (geometryType.toLowerCase()) {
        case "point":
        case "multipoint":
          return "red";
        case "linestring":
        case "multilinestring":
          return "blue";
        case "polygon":
        case "multipolygon":
          return "green";
        case "None":
          return "grey";
        default:
          return "grey";
      }
    },

    getDefaultColorForGeometryType(geometryType: string): string {
      switch (geometryType.toLowerCase()) {
        case "point":
        case "multipoint":
          return "#ff3333"; // Red
        case "linestring":
        case "multilinestring":
          return "#3388ff"; // Blue
        case "polygon":
        case "multipolygon":
          return "#33ff33"; // Green
        case "None":
          return "#888888"; // Grey
        default:
          return "#888888"; // Grey
      }
    },

    getLayerById(layerId: number) {
      // First check the store's selected layers
      const selectedLayers = this.geoPackageStore.getSelectedLayers;
      const layer = selectedLayers.find((l) => l.id === layerId);

      if (layer) return layer;

      // As a fallback, search through all layers in all files
      for (const file of this.geoPackageStore.files) {
        const layers = this.geoPackageStore.getLayersForFile(file.id);
        const fileLayer = layers.find((l) => l.id === layerId);
        if (fileLayer) return fileLayer;
      }

      return { layer_name: "Unknown Layer", geometry_type: "Unknown" };
    },

    async importLayers() {
      if (this.selectedLayerIds.length === 0) return;

      this.isImporting = true;
      this.importProgress = 0;
      this.currentLayerIndex = 0;
      this.currentLayerName = "";

      try {
        // Get selected layers from store
        const selectedLayers = this.geoPackageStore.getSelectedLayers;

        // Process layers one by one
        for (let i = 0; i < this.selectedLayerIds.length; i++) {
          const layerId = this.selectedLayerIds[i];
          this.currentLayerIndex = i + 1; // Update current layer index

          try {
            console.log(
              `Processing layer ${layerId} (${i + 1}/${this.selectedLayerIds.length})`,
            );

            // Get layer info
            const layer =
              selectedLayers.find((l) => l.id === layerId) ||
              this.getLayerById(layerId);

            if (!layer) {
              console.error(`Could not find layer with ID ${layerId}`);
              continue;
            }

            // Get configured display name and style
            const displayName =
              this.layerDisplayNames[layerId] || layer.layer_name;
            this.currentLayerName = displayName; // Update current layer name

            const style = this.layerStyles[layerId] || {
              color: this.getDefaultColorForGeometryType(layer.geometry_type),
              opacity: 0.8,
            };
            console.log("First Style:", style);

            // Create a layer group first - we'll add features to this incrementally
            const layerGroup = L.layerGroup();

            // Add empty layer group to the map immediately with style
            const layerGroupId = `geopackage-${layerId}-${displayName.replace(/\s+/g, "_")}`;
            if (this.mapUtilsObj) {
              await this.mapUtilsObj.addLayerGroup(
                layerGroupId,
                layerGroup,
                displayName,
                style,
                true, // fitBounds
                true, // imported - mark as imported through wizard
              );
            } else {
              console.warn(
                "mapUtilsObj not available, falling back to mapStore",
              );
              await this.mapStore.addLayerGroup(
                layerGroupId,
                layerGroup,
                displayName,
                style,
                true, // fitBounds
                true, // imported - mark as imported through wizard
              );
            }

            // Get current zoom level for appropriate simplification
            const currentZoom =
              this.mapUtilsObj?.mapObject?.getZoom() ||
              this.mapStore.mapObject?.getZoom() ||
              10;

            // Determine batch size based on geometry type
            const batchSize = layer.geometry_type
              .toLowerCase()
              .includes("point")
              ? 1000
              : layer.geometry_type.toLowerCase().includes("line")
                ? 500
                : 250;

            // Process in batches
            let offset = 0;
            let hasMore = true;
            let totalFeatures = 0;
            let processedFeatures = 0;

            // First, try to get total feature count if available
            try {
              const countResponse = await GeoPackageService.getLayerFeatures(
                layerId,
                undefined,
                1,
                0,
              );
              // Use countResponse directly
              if (countResponse?.metadata?.totalFeatures) {
                totalFeatures = countResponse.metadata.totalFeatures;
              }
            } catch (error) {
              console.warn(
                `Could not get total features for layer ${layerId}:`,
                error,
              );
            }

            while (hasMore) {
              // Allow UI to update before fetching next batch
              await this.waitForNextFrame();

              console.log(
                `Fetching features for layer ${layerId}, batch at offset ${offset}`,
              );
              let geoJsonData;
              try {
                // Fetch one batch of features
                const response = await GeoPackageService.getLayerFeatures(
                  layerId,
                  undefined, // bbox
                  batchSize,
                  offset,
                  undefined, // Skip simplify parameter
                  undefined, // filter
                );

                geoJsonData = response;

                // Debug log to see the structure
                console.log(`Response type for layer ${layerId}:`, {
                  responseType: typeof response,
                  isFeatureCollection: response?.type === "FeatureCollection",
                  featuresArray: Array.isArray(response?.features),
                  featureCount: response?.features?.length || 0,
                });

                // Check for valid GeoJSON format
                if (
                  !geoJsonData ||
                  typeof geoJsonData !== "object" ||
                  geoJsonData.type !== "FeatureCollection" ||
                  !Array.isArray(geoJsonData.features)
                ) {
                  console.error(
                    `Invalid GeoJSON format for layer ${layerId}:`,
                    geoJsonData,
                  );
                  break;
                }

                const features = geoJsonData.features;

                // If no features returned, we're done
                if (features.length === 0) {
                  hasMore = false;
                  break;
                }

                processedFeatures += features.length;
                console.log(`Processing batch of ${features.length} features`);

                // Update progress
                if (totalFeatures > 0) {
                  // If we know total features, calculate exact progress for this layer
                  const layerProgress =
                    (processedFeatures / totalFeatures) * 100;
                  // Overall progress is a combination of layer progress and layer index
                  this.importProgress =
                    (i / this.selectedLayerIds.length) * 100 +
                    layerProgress / this.selectedLayerIds.length;
                } else {
                  // If we don't know total features, just update based on layer index
                  this.importProgress =
                    (i / this.selectedLayerIds.length) * 100;
                }

                // Try to extract style information from feature properties
                let layerStyle = this.createLayerStyle(
                  layerId,
                  layer.geometry_type,
                );

                console.log("Layer Style:", layerStyle);

                // Create leaflet style object
                // If we have a custom icon, only include icon and radius (size)
                // PNG icons have their own colors, so we don't need color/opacity
                const leafletStyle: any = {};

                if (layerStyle.icon) {
                  // For custom icons, only store icon path and size
                  leafletStyle.icon = layerStyle.icon;
                  leafletStyle.radius = layerStyle.radius || 12; // Default icon size
                } else {
                  // For standard markers/circles, include all styling
                  leafletStyle.color = layerStyle.color || "#3388ff";
                  leafletStyle.weight = layerStyle.weight || 2;
                  leafletStyle.opacity = layerStyle.opacity || 0.8;
                  leafletStyle.fillOpacity = layerStyle.fillOpacity || 0.6;
                  leafletStyle.fillColor =
                    layerStyle.fillColor || layerStyle.color || "#3388ff";
                  leafletStyle.radius = layerStyle.radius || 8;
                }

                console.log("Leaflet style:", leafletStyle);

                // Process features in even smaller chunks if needed
                const chunkSize = layer.geometry_type
                  .toLowerCase()
                  .includes("polygon")
                  ? 50
                  : 200;

                for (let j = 0; j < features.length; j += chunkSize) {
                  // Allow UI to update between chunks
                  if (j > 0) await this.waitForNextFrame();

                  const featureChunk = features.slice(j, j + chunkSize);

                  // Apply client-side simplification to this chunk
                  const simplifiedChunk = {
                    type: "FeatureCollection",
                    features: featureChunk,
                  };

                  // Apply geometry simplification based on zoom level
                  const simplifiedData = simplifyGeoJson(
                    simplifiedChunk,
                    currentZoom,
                  );

                  const chunkLayer = L.geoJSON(simplifiedData, {
                    style: () => leafletStyle,
                    pointToLayer: (feature, latlng) => {
                      if (layer.geometry_type.toLowerCase().includes("point")) {
                        // Check if layer style has a custom icon
                        if (leafletStyle.icon) {
                          console.log(
                            "Creating custom icon marker. Icon ID:",
                            leafletStyle.icon,
                          );
                          // Create custom icon from database via API using icon_id
                          const iconUrl = `${this.apiBaseUrl}/geopackage/icon/${leafletStyle.icon}`;
                          // Use a reasonable default size (32px) or scale the radius if it's large enough
                          // QGIS size in millimeters needs significant scaling for web display
                          let iconSize = 24; // Default size in pixels
                          if (leafletStyle.radius && leafletStyle.radius > 5) {
                            // If radius is reasonably large, use it (multiply by 4 for better visibility)
                            iconSize = leafletStyle.radius * 4;
                          }
                          console.log("Custom icon URL:", iconUrl);
                          console.log("Custom icon size:", iconSize);

                          const customIcon = L.icon({
                            iconUrl: iconUrl,
                            iconSize: [iconSize, iconSize],
                            iconAnchor: [iconSize / 2, iconSize / 2],
                            popupAnchor: [0, -iconSize / 2],
                          });

                          return L.marker(latlng, { icon: customIcon });
                        }

                        console.log("No custom icon, using circle marker");

                        // Fall back to circle marker for layers without custom icons
                        return L.circleMarker(latlng, {
                          ...leafletStyle,
                          radius: 8,
                        });
                      }
                      return L.marker(latlng);
                    },
                    onEachFeature: (feature, layer) => {
                      // Only add basic popup with limited properties
                      if (feature.properties) {
                        let popupContent = `<h4>${displayName}</h4>`;
                        const propEntries = Object.entries(feature.properties)
                          .filter(
                            ([_, value]) =>
                              value !== null && value !== undefined,
                          )
                          .slice(0, 5); // Limit to just 5 properties for performance

                        if (propEntries.length > 0) {
                          popupContent += propEntries
                            .map(
                              ([key, value]) =>
                                `<strong>${key}:</strong> ${String(value)}`,
                            )
                            .join("<br>");
                          layer.bindPopup(popupContent);
                        }
                      }
                    },
                  });

                  // Add this chunk to the layer group
                  layerGroup.addLayer(chunkLayer);
                }

                // Update offset for next batch
                offset += features.length;

                // Check if we need to continue fetching
                hasMore = geoJsonData.metadata
                  ? !!geoJsonData.metadata.hasMore
                  : features.length === batchSize;
              } catch (batchError) {
                console.error(
                  `Error processing batch for layer ${layerId}:`,
                  batchError,
                );
                // Continue with next batch even if this one failed
                offset += batchSize;
              }
            }

            console.log(
              `Finished loading layer ${displayName} (ID: ${layerId})`,
            );
          } catch (layerError) {
            console.error(`Error processing layer ${layerId}:`, layerError);
          }

          // Update progress to show completion of this layer
          this.importProgress = ((i + 1) / this.selectedLayerIds.length) * 100;

          // Add a small delay between processing layers
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Successfully imported all layers
        // Emit event to notify TheMapEditor to refresh the layer list
        eventBus.emit("geopackage:layers-imported");

        // Close the dialog
        this.closeDialog();
      } catch (error) {
        console.error("Error importing layers:", error);
      } finally {
        this.isImporting = false;
        this.importProgress = 0;
        this.currentLayerName = "";
        this.currentLayerIndex = 0;
      }
    },

    createLayerStyle(layerId, geometryType) {
      // Get the user-configured style or default
      const configuredStyle = this.layerStyles[layerId] || {};

      // Get the default style based on geometry type
      const defaultStyle = this.detectLayerStyleFromGeometry(geometryType);

      // Merge configured style with defaults
      return {
        ...defaultStyle,
        ...configuredStyle,
        // Ensure fillColor is set for polygons if only color is specified
        fillColor:
          configuredStyle.fillColor ||
          configuredStyle.color ||
          defaultStyle.fillColor ||
          defaultStyle.color,
      };
    },

    waitForNextFrame(): Promise<void> {
      return new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => {
          resolve();
        });
      });
    },

    closeDialog() {
      this.showUploadDialog = false;

      // Defer clearing state until after dialog animation completes
      // This prevents the "no layers selected" warning from flashing
      setTimeout(() => {
        this.step = 1;
        this.fileInput = null;
        this.uploadError = "";
        this.selectedLayerIds = [];
        this.layerDisplayNames = {};
        this.layerStyles = {};
        this.geoPackageStore.clearSelection();

        // Reset progress indicators
        this.importProgress = 0;
        this.currentLayerName = "";
        this.currentLayerIndex = 0;
        this.isImporting = false;
      }, 300); // Wait for dialog animation to complete (typical dialog animation is 200-300ms)
    },

    confirmDelete(geoPackage: any) {
      this.geoPackageToDelete = geoPackage;
      this.showDeleteDialog = true;
    },

    async deleteGeoPackage() {
      if (!this.geoPackageToDelete) return;

      this.isDeleting = true;
      try {
        // Call the delete service
        await GeoPackageService.deleteGeoPackage(this.geoPackageToDelete.id);

        // Get all layers for this GeoPackage
        const layersToRemove = this.geoPackageStore.getLayersForFile(
          this.geoPackageToDelete.id,
        );

        // Remove each layer from the map if it's currently displayed
        if (this.mapUtilsObj) {
          layersToRemove.forEach((layer) => {
            this.mapUtilsObj.removeLayerGroup(layer.id.toString());
          });
        }

        // Refresh the GeoPackage list from the server
        await this.geoPackageStore.fetchGeoPackages(
          this.officeStore.selectedProject.id,
        );

        // Clear selected layers that belonged to this GeoPackage
        this.selectedLayerIds = this.selectedLayerIds.filter(
          (layerId) => !layersToRemove.some((layer) => layer.id === layerId),
        );

        // Close the delete dialog
        this.showDeleteDialog = false;
        this.geoPackageToDelete = null;
      } catch (error) {
        console.error("Error deleting GeoPackage:", error);
        // You could show an error message to the user here
        alert(
          "Fehler beim Löschen der GeoPackage-Datei. Bitte versuchen Sie es erneut.",
        );
      } finally {
        this.isDeleting = false;
      }
    },
  },
});
</script>

<style scoped>
.geo-upload-btn {
  text-align: left;
  justify-content: flex-start;
}

.border-b {
  border-bottom: 1px solid #eee;
}
</style>
